import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { oauthService } from '@/services/oauthService';

export default function OAuthCallback() {
  const { platform } = useParams<{ platform: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      if (!platform) {
        console.error('Platform not specified');
        navigate('/settings');
        return;
      }

      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('Authorization code not found');
        }

        // Verify state (optional for some platforms due to redirect issues)
        const storedState = sessionStorage.getItem(`oauth_state_${platform}`);
        if (storedState && state !== storedState) {
          console.warn('State parameter mismatch, but continuing...');
        }

        // Clean up state
        if (storedState) {
          sessionStorage.removeItem(`oauth_state_${platform}`);
        }

        // Exchange code for tokens
        const tokens = await oauthService.exchangeCodeForToken(platform, code);
        
        // Get user profile
        const userProfile = await oauthService.getUserProfile(platform, tokens.accessToken);
        
        // Store tokens and profile
        // For YouTube, use 'google' as the storage key since YouTube uses Google OAuth
        const storageKey = platform === 'youtube' ? 'google' : platform;
        
        localStorage.setItem(`${storageKey}_access_token`, tokens.accessToken);
        if (tokens.refreshToken) {
          localStorage.setItem(`${storageKey}_refresh_token`, tokens.refreshToken);
        }
        if (tokens.expiresIn) {
          const expiresAt = Date.now() + (tokens.expiresIn * 1000);
          localStorage.setItem(`${storageKey}_expires_at`, expiresAt.toString());
        }
        
        localStorage.setItem(`${storageKey}_user_profile`, JSON.stringify(userProfile));
        localStorage.setItem(`${storageKey}_connected_at`, new Date().toISOString());

        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('oauth_connected', {
          detail: { platform, userProfile }
        }));

        // OAuth connection successful

        // Redirect back to settings
        const returnUrl = localStorage.getItem('oauth_return_url') || '/settings';
        localStorage.removeItem('oauth_return_url');
        navigate(returnUrl);

      } catch (error) {
        console.error('OAuth callback error:', error);
        // Show error message and redirect to settings
        alert(`OAuth connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        navigate('/settings');
      }
    };

    handleCallback();
  }, [platform, navigate]);


  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Connecting to {platform}...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
            <p className="text-center text-muted-foreground">
              Please wait while we complete the connection...
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
